/**
 * /dashboard/performance/one-on-ones/$id — 1:1 detail (Phase 7.3a).
 *
 * Uses the single-GET endpoint GET /performance/1-1s/{id}.
 * private_manager_notes is omitempty server-side.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/performance/one-on-ones/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useOneOnOne } from "@/hooks/use-performance";
import { toApiError } from "@/lib/api";
import { PerformanceModuleShell } from "@/components/shared/performance-module-shell";
import { PerformanceFeatureGate } from "@/components/performance/performance-feature-gate";
import { OneOnOneDetail } from "@/components/performance/one-on-one-detail";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/performance/one-on-ones/$id",
)({
  component: OneOnOneDetailRoute,
});

function Body({ id }: { id: number }) {
  const q = useOneOnOne(id);

  if (q.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (q.isError || !q.data) {
    const e = toApiError(q.error);
    return (
      <Alert variant="destructive">
        <AlertTitle>
          {e.error === "not_found"
            ? "1:1 not found"
            : "Could not load this 1:1"}
        </AlertTitle>
        <AlertDescription>
          {e.error === "not_found"
            ? "This 1:1 does not exist or you are not a participant."
            : e.message}
        </AlertDescription>
      </Alert>
    );
  }
  return <OneOnOneDetail oneOnOne={q.data} />;
}

function OneOnOneDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);

  return (
    <PerformanceModuleShell
      title="1:1"
      description="Agenda, shared notes, and meeting status."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/performance/one-on-ones" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back to 1:1s
          </Link>
        </Button>
      }
    >
      <PerformanceFeatureGate>
        {Number.isFinite(numId) && numId > 0 ? (
          <Body id={numId} />
        ) : (
          <p className="text-sm text-muted-foreground">
            Invalid 1:1 id.
          </p>
        )}
      </PerformanceFeatureGate>
    </PerformanceModuleShell>
  );
}
